import {
  STORE_CONFIRM_SIZES_CUSTOM_SIZE_START_CODE,
  STORE_CONFIRM_SIZES_EXT_DEFAULT_SIZES,
  STORE_CONFIRM_SIZES_SIZES_OPTIONS,
} from '../../constants/storeCheckout.constants';
import type { ISizeSelectedState } from './storeConfirmSizes.types';
import { STANDARD_SIZES } from '@/constants/Order';

export const getSizesLabel = (sizes: string): string[] => {
  if (!sizes) return [];
  return sizes
    .split(';')
    .filter(Boolean)
    .map((pair) => pair.split(':')[1])
    .filter(Boolean);
};

export const parseOrderItemSizes = (sizesString: string): Record<string, boolean> => {
  if (!sizesString) return {};

  return sizesString
    .split(';')
    .filter(Boolean)
    .reduce<Record<string, boolean>>((acc, pair) => {
      const parts = pair.split(':');
      if (parts.length === 3) {
        const [, size, value] = parts;
        if (size && value !== undefined) {
          acc[size] = value === '1';
        }
      }
      return acc;
    }, {});
};

export const buildOrderItemSizesFromDesignAndSelected = (
  designApparelSizes: string[],
  orderItemSizesString: string,
  legacyApparelSizesString = ''
): Record<string, boolean> => {
  const selectedSizesMap = orderItemSizesString
    ? parseOrderItemSizes(orderItemSizesString)
    : parseOrderItemSizes(legacyApparelSizesString);

  const hasExplicitSelection = Object.keys(selectedSizesMap).length > 0;

  if (!designApparelSizes.length) {
    return selectedSizesMap;
  }

  const orderItemSizes = designApparelSizes.reduce<Record<string, boolean>>((acc, size) => {
    acc[size] = hasExplicitSelection ? Boolean(selectedSizesMap[size]) : true;
    return acc;
  }, {});

  Object.entries(selectedSizesMap).forEach(([size, selected]) => {
    if (selected && !(size in orderItemSizes)) {
      orderItemSizes[size] = true;
    }
  });

  return orderItemSizes;
};

export const parseSizeCodeMappings = (sizesString: string): Record<string, string> => {
  if (!sizesString) return {};

  return sizesString
    .split(';')
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, pair) => {
      const [code, label] = pair.split(':');
      if (code && label) {
        acc[label] = code;
      }
      return acc;
    }, {});
};

const standardSizesList: readonly string[] = STANDARD_SIZES;

const isStandardSizeLabel = (sizeLabel: string): boolean => standardSizesList.includes(sizeLabel);

export const buildSizeConfigurationLikeOrders = (
  hasExternalApparel: boolean,
  orderItemSizes: Record<string, boolean>,
  designApparelSizes: string[]
): ISizeSelectedState[] => {
  const standardSizesSet = new Set<string>(STANDARD_SIZES);
  const designApparelSizesSet = new Set(designApparelSizes);

  if (hasExternalApparel) {
    const extDefaultSizesSet = new Set<string>(STORE_CONFIRM_SIZES_EXT_DEFAULT_SIZES);

    const allExternalSizes = new Set([
      ...STORE_CONFIRM_SIZES_EXT_DEFAULT_SIZES,
      ...STANDARD_SIZES,
      ...designApparelSizes,
      ...Object.keys(orderItemSizes),
    ]);

    return Array.from(allExternalSizes).map((size) => {
      const isItemSize = orderItemSizes[size] || false;
      const isDesignSize = designApparelSizesSet.has(size);
      const isStandardSize = standardSizesSet.has(size);
      const isExtDefaultSize = extDefaultSizesSet.has(size);
      const wasAddedByUser = isItemSize && !isDesignSize && !isStandardSize && !isExtDefaultSize;

      return {
        label: size,
        disabled: false,
        selected: isItemSize,
        standard: isStandardSize || isDesignSize || isExtDefaultSize,
        addedByUser: wasAddedByUser,
      };
    });
  }

  const allUniqueSizes = new Set([...designApparelSizes, ...Object.keys(orderItemSizes)]);

  return Array.from(allUniqueSizes).map((size) => {
    const isDesignSize = designApparelSizesSet.has(size);
    const isItemSize = orderItemSizes[size] || false;
    const isStandardSize = standardSizesSet.has(size);
    const wasAddedByUser = isItemSize && !isDesignSize && !isStandardSize;

    return {
      label: size,
      disabled: !isDesignSize && !isItemSize,
      selected: isItemSize,
      standard: isStandardSize || isDesignSize,
      addedByUser: wasAddedByUser,
    };
  });
};

export const sizesSelectedToRecord = (
  sizeConfig: ISizeSelectedState[]
): Record<string, ISizeSelectedState> =>
  sizeConfig.reduce<Record<string, ISizeSelectedState>>((acc, size) => {
    acc[size.label] = { ...size };
    return acc;
  }, {});

export const sizesAvailableToRecord = (sizeConfig: ISizeSelectedState[]): Record<string, boolean> =>
  sizeConfig.reduce<Record<string, boolean>>((acc, size) => {
    acc[size.label] = size.selected;
    return acc;
  }, {});

export const sortSizesForDisplay = (sizes: ISizeSelectedState[]): ISizeSelectedState[] => {
  const orderMap = new Map<string, number>(
    STORE_CONFIRM_SIZES_SIZES_OPTIONS.map((s, index) => [s.value, index])
  );

  const osSize = sizes.find((s) => s.label === 'OS' && s.selected);
  const otherSizes = sizes.filter((s) => !(s.label === 'OS' && s.selected));

  const standardSizes = otherSizes
    .filter((s) => s.standard)
    .sort((a, b) => (orderMap.get(a.label) ?? 1e9) - (orderMap.get(b.label) ?? 1e9));
  const customSizes = otherSizes.filter((s) => !s.standard);

  return [...(osSize ? [osSize] : []), ...standardSizes, ...customSizes];
};

export const convertSizesToOrderItemFormat = (
  sizesSelected: Record<string, ISizeSelectedState>,
  allowedSizes?: string
): string => {
  const sizeCodeMappings = parseSizeCodeMappings(allowedSizes ?? '');
  let customSizeCode = STORE_CONFIRM_SIZES_CUSTOM_SIZE_START_CODE;

  return Object.values(sizesSelected)
    .map((size) => {
      if (!size?.label) return '';
      const existingCode = sizeCodeMappings[size.label];
      const code = existingCode ?? String(customSizeCode);
      if (!existingCode) {
        customSizeCode -= 1;
      }
      return size.selected ? `${code}:${size.label}:1` : '';
    })
    .filter(Boolean)
    .join(';');
};

export const convertOrderItemSizesToAllowedSizes = (sizesString: string): string =>
  sizesString
    .split(';')
    .filter(Boolean)
    .map((entry) => {
      const [code, label] = entry.split(':');

      if (!code || !label) {
        return '';
      }

      return `${code}:${label}`;
    })
    .filter(Boolean)
    .join(';');

export const convertAllowedSizesToOrderItemFormat = (
  sizesSelected: Record<string, ISizeSelectedState>,
  allowedSizes?: string
): string =>
  convertOrderItemSizesToAllowedSizes(convertSizesToOrderItemFormat(sizesSelected, allowedSizes));

export const getSelectedSizeLabels = (
  sizesSelected: Record<string, ISizeSelectedState>
): string[] =>
  Object.values(sizesSelected)
    .filter((size) => size.selected)
    .map((size) => size.label);

export const isStandardSizeLabelCheck = isStandardSizeLabel;
